import axios, { AxiosError } from 'axios';
import { config } from '../config';

const api = axios.create({
  baseURL: config.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (requestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    return requestConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - redirect to login
      localStorage.removeItem('accessToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API types
export interface MeResponse {
  email: string;
  email_verified: boolean;
  sub: string;
  is_demo_editor: boolean;
}

export interface DemoSubmitRequest {
  title: string;
  demo_url: string;
  source_type: string;
  summary: string;
  asset_type?: string;  // Required in UI but optional in interface for flexibility
  platforms: string[];  // At least one required
  use_cases: string[];  // At least one required
  functionalities: string[];  // At least one required
  industries: string[];  // At least one required
  personas: string[];  // At least one required
  tags?: string[];  // Free-text searchable tags
  tier: string;  // Required
  owner?: string;
  owner_email: string;  // Required
  salesforce_opportunity_id?: string;  // Salesforce Opportunity ID
  crga_available?: boolean;
  needs_core_team?: boolean;
  pr_api_available?: boolean;
  answer_api_available?: boolean;
  mcp_available?: boolean;
  script_or_flow_or_clickpath?: string;
  limitations?: string;
  related_resource_urls: string[];
  demo_recording_url?: string;
}

export interface DemoSubmitResponse {
  demo_id: string;
  status: string;
  message: string;
}

// Alias for backward compatibility with SubmitPage
export type DemoSubmitPayload = DemoSubmitRequest & {
  tagline?: string;
  crga_link?: string;
  notes?: string;
  demo_recording_url?: string;
  owner_name: string;  // Required field
  owner_email: string; // Required field
  asset_type?: string;
};

export interface DemoUpdateRequest {
  title?: string;
  demo_url?: string;
  source_type?: string;
  summary?: string;
  platforms?: string[];
  use_cases?: string[];
  functionalities?: string[];
  industries?: string[];
  personas?: string[];
  tier?: string;
  owner?: string;
  owner_email?: string;
  salesforce_opportunity_id?: string;
  crga_available?: boolean;
  needs_core_team?: boolean;
  script_or_flow_or_clickpath?: string;
  limitations?: string;
  related_resource_urls?: string[];
  demo_recording_url?: string;
  status?: string;
}

export interface DemoUpdateResponse {
  demo_id: string;
  status: string;
  message: string;
}

export interface DemoDetails {
  demo_id: string;
  title: string;
  demo_url: string;
  source_type: string;
  summary: string;
  platforms: string[];
  use_cases: string[];
  functionalities: string[];
  industries: string[];
  personas: string[];
  tags?: string[];  // Free-text searchable tags
  tier?: string;
  owner?: string;
  owner_email?: string;
  salesforce_opportunity_id?: string;
  crga_available?: boolean;
  needs_core_team?: boolean;
  pr_api_available?: boolean;
  answer_api_available?: boolean;
  mcp_available?: boolean;
  script_or_flow_or_clickpath?: string;
  limitations?: string;
  related_resource_urls?: string[];
  demo_recording_url?: string;
  image_url?: string;
  favicon_url?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

// Taxonomy response from /api/taxonomy
export interface TaxonomyResponse {
  version: string;
  asset_type: string[];
  platforms: string[];
  functionalities: string[];
  use_cases: string[];
  personas: string[];
  industries: string[];
  owners: string[];
  source_types: string[];
  tiers: string[];
}

// Admin API types
export interface AdminDemoListItem {
  demo_id: string;
  title: string;
  status: string;
  owner?: string;
  owner_email?: string;
  tier?: string;
  updated_at: string;
  last_validated_date?: string;
}

export interface AdminDemoListResponse {
  demos: AdminDemoListItem[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface AdminDemoUpdateRequest {
  title?: string;
  demo_url?: string;
  source_type?: string;
  summary?: string;
  platforms?: string[];
  use_cases?: string[];
  functionalities?: string[];
  industries?: string[];
  personas?: string[];
  tags?: string[];  // Free-text searchable tags
  tier?: string;
  owner?: string;
  owner_email?: string;
  crga_available?: boolean;
  needs_core_team?: boolean;
  pr_api_available?: boolean;
  answer_api_available?: boolean;
  mcp_available?: boolean;
  script_or_flow_or_clickpath?: string;
  limitations?: string;
  related_resource_urls?: string[];
  status?: string;
  last_validated_date?: string;
}

export interface AdminDemoResponse {
  demo_id: string;
  status: string;
  message: string;
}

export interface LogoTaxonomyResponse {
  version: string;
  industries: string[];
  sub_industries: string[];
  verticals: string[];
  geographic_regions: string[];
  solution_categories: string[];
  use_cases: string[];
  personas: string[];
  platforms: string[];
  customer_types: string[];
  deployment_statuses: string[];
  implementation_types: string[];
  regions: string[];
  legal_approval_statuses: string[];
  background_styles: string[];
  logo_usage_guidelines: string[];
  billing_countries: string[];
}

export interface LogoRecord {
  logo_id: string;
  record_type: string;
  account_id: string;
  account_name: string;
  customer_name: string;
  display_name: string;
  website?: string;
  billing_country?: string;
  geographic_region?: string;
  industry?: string;
  sub_industry?: string;
  vertical?: string;
  image_filename: string;
  image_extension: string;
  original_image_url: string;
  logo_usage_guideline: string;
  solution_categories: string[];
  use_cases: string[];
  gen_ai: boolean;
  pr_api: boolean;
  data_source: string;
  created_at: string;
  updated_at: string;
}

export interface ProofPointMetricValue {
  [metric: string]: string | number | null | undefined;
}

export interface ProofPoint {
  proof_point_id?: string;
  source_sheet?: string;
  source_row?: number;
  bv_category?: string;
  disclosure_policy?: string;
  client_advocacy?: string;
  metrics?: ProofPointMetricValue;
  resource_links?: string[] | null;
}

export interface CustomerStory {
  title: string;
  link: string;
}

export interface LogoSubmitRequest {
  customer_name: string;
  display_name?: string;
  salesforce_account_id: string;
  industries: string[];
  use_cases: string[];
  personas: string[];
  platforms: string[];
  implementation_type?: string;
  customer_type?: string;
  submission_owner_name?: string;
  submission_owner_email?: string;
  notes?: string;
  tags?: string[];
  reference_story?: string;
  region?: string;
  country?: string;
  is_public_reference_allowed?: boolean;
  legal_approval_status?: string;
  logo_background_style?: string;
  website?: string;
  billing_country?: string;
  geographic_region?: string;
  industry?: string;
  sub_industry?: string;
  vertical?: string;
  logo_usage_guideline?: string;
  solution_categories?: string[];
  gen_ai?: boolean;
  pr_api?: boolean;
  features?: string[];
  search_keywords?: string[];
  customer_stories?: CustomerStory[];
}

export interface LogoSubmitResponse {
  logo_id: string;
  deployment_status: string;
  message: string;
  salesforce_enriched: boolean;
  indexing_status: 'indexed' | 'pending';
}

export interface LogoSubmissionStatus {
  enabled: boolean;
  reason?: 'operationally_disabled';
}

export interface LogoDetails {
  logo_id: string;
  customer_name: string;
  display_name?: string;
  salesforce_opportunity_id?: string;
  salesforce_account_id?: string;
  salesforce_account_name?: string;
  opportunity_name?: string;
  industries: string[];
  use_cases: string[];
  personas: string[];
  platforms: string[];
  implementation_type?: string;
  customer_type: string;
  deployment_status: string;
  logo_background_style: string;
  submission_owner_name?: string;
  submission_owner_email?: string;
  submitted_by_email?: string;
  notes?: string;
  tags: string[];
  reference_story?: string;
  region?: string;
  country?: string;
  is_public_reference_allowed: boolean;
  legal_approval_status: string;
  original_image_url?: string;
  normalized_image_url?: string;
  thumbnail_image_url?: string;
  // Read-only metadata used by the logo details display. Proof points remain
  // import-managed; the UI never sends this field back to the API.
  proof_points?: ProofPoint[];
  created_at: string;
  updated_at: string;
}

export interface AdminProofPoint {
  bv_category?: string;
}

export interface AdminLogoListItem {
  logo_id: string;
  account_id: string;
  customer_name: string;
  industry?: string;
  vertical?: string;
  logo_usage_guideline?: string;
  proof_points?: AdminProofPoint[] | null;
  updated_at: string;
  // Editable fields included for inline-edit pre-population
  solution_categories?: string[];
  use_cases?: string[];
  geographic_region?: string;
  sub_industry?: string;
  website?: string;
  billing_country?: string;
  features?: string[];
}

export interface AdminLogoListResponse {
  logos: AdminLogoListItem[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface AdminLogoUpdateRequest {
  logo_usage_guideline?: string;
  solution_categories?: string[];
  use_cases?: string[];
  industry?: string;
  vertical?: string;
  geographic_region?: string;
  sub_industry?: string;
  website?: string;
  billing_country?: string;
  features?: string[];
  replace_customer_stories?: CustomerStory[];
  search_keywords?: string[];
}

export interface AdminLogoResponse {
  logo_id: string;
  message: string;
}

export interface SalesforceLookupResponse {
  account_id: string;
  account_name: string;
  website: string | null;
  billing_country: string | null;
  geographic_region: string | null;
  industry: string | null;
  sub_industry: string | null;
  vertical: string | null;
  logo_usage_guideline: string | null;
  solution_categories: string[];
  use_cases: string[];
  gen_ai: boolean;
  pr_api: boolean;
  has_qualifying_order_items: boolean;
  features: string[];
}

// API functions
export const apiService = {
  // Health check
  health: async () => {
    const response = await api.get('/health');
    return response.data;
  },

  // Get current user
  getMe: async (): Promise<MeResponse> => {
    const response = await api.get<MeResponse>('/me');
    return response.data;
  },

  // Get taxonomy (standardized vocabulary for facet fields)
  getTaxonomy: async (): Promise<TaxonomyResponse> => {
    const response = await api.get<TaxonomyResponse>('/taxonomy');
    return response.data;
  },

  getLogoTaxonomy: async (): Promise<LogoTaxonomyResponse> => {
    const response = await api.get<LogoTaxonomyResponse>('/logos/taxonomy');
    return response.data;
  },

  getLogoSubmissionStatus: async (): Promise<LogoSubmissionStatus> => {
    const response = await api.get<LogoSubmissionStatus>('/logos/submission-status');
    return response.data;
  },

  getLogoUsageGuidelines: async (): Promise<Record<string, string>> => {
    const response = await api.get<Record<string, string>>('/logos/usage-guidelines');
    return response.data;
  },

  // Submit a new demo
  submitDemo: async (data: DemoSubmitRequest, heroImage: File): Promise<DemoSubmitResponse> => {
    const formData = new FormData();
    formData.append('payload', JSON.stringify(data));
    formData.append('image_file', heroImage);
    const response = await api.post<DemoSubmitResponse>('/demos/submit', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Get demo by ID
  getDemo: async (demoId: string): Promise<DemoDetails> => {
    const response = await api.get<DemoDetails>(`/demos/${demoId}`);
    return response.data;
  },

  submitLogo: async (data: LogoSubmitRequest, logoImage: File): Promise<LogoSubmitResponse> => {
    const formData = new FormData();
    formData.append('payload', JSON.stringify(data));
    formData.append('image_file', logoImage);
    const response = await api.post<LogoSubmitResponse>('/logos/submit', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getLogo: async (logoId: string): Promise<LogoDetails> => {
    const response = await api.get<LogoDetails>(`/logos/${logoId}`);
    return response.data;
  },

  listRecentLogos: async (limit: number = 24): Promise<{ logos: LogoDetails[] }> => {
    const response = await api.get<{ logos: LogoDetails[] }>('/logos/recent', { params: { limit } });
    return response.data;
  },

  // Update an existing demo (removed - now admin-only)
  // Demo editing has moved to /api/admin/demos/:demo_id
  // Use adminUpdateDemo for privileged editing

  // Get OG image for a URL
  getOgImage: async (url: string): Promise<{ image_url: string | null; favicon_url: string | null }> => {
    try {
      const response = await api.get<{ image_url: string | null; favicon_url: string | null }>('/og-image', {
        params: { url },
      });
      return { image_url: response.data.image_url, favicon_url: response.data.favicon_url };
    } catch {
      return { image_url: null, favicon_url: null };
    }
  },

  // ======================
  // ADMIN API (privileged)
  // ======================
  
  // List demos for admin management
  adminListDemos: async (
    page: number = 1,
    pageSize: number = 20,
    status?: string,
    search?: string
  ): Promise<AdminDemoListResponse> => {
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (status) params.status = status;
    if (search) params.search = search;
    const response = await api.get<AdminDemoListResponse>('/admin/demos', { params });
    return response.data;
  },

  // Get demo for admin editing
  adminGetDemo: async (demoId: string): Promise<DemoDetails> => {
    const response = await api.get<DemoDetails>(`/admin/demos/${demoId}`);
    return response.data;
  },

  // Update demo (admin-only)
  adminUpdateDemo: async (demoId: string, data: AdminDemoUpdateRequest): Promise<AdminDemoResponse> => {
    const response = await api.put<AdminDemoResponse>(`/admin/demos/${demoId}`, data);
    return response.data;
  },

  // Upload demo image (admin-only)
  adminUploadImage: async (demoId: string, imageFile: File): Promise<AdminDemoResponse> => {
    const formData = new FormData();
    formData.append('image_file', imageFile);
    const response = await api.post<AdminDemoResponse>(`/admin/demos/${demoId}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Promote demo to Active status
  adminPromoteDemo: async (demoId: string): Promise<AdminDemoResponse> => {
    const response = await api.post<AdminDemoResponse>(`/admin/demos/${demoId}/promote`);
    return response.data;
  },

  // Retire demo
  adminRetireDemo: async (demoId: string): Promise<AdminDemoResponse> => {
    const response = await api.post<AdminDemoResponse>(`/admin/demos/${demoId}/retire`);
    return response.data;
  },

  // Unretire demo (restore to Active)
  adminUnretireDemo: async (demoId: string): Promise<AdminDemoResponse> => {
    const response = await api.post<AdminDemoResponse>(`/admin/demos/${demoId}/unretire`);
    return response.data;
  },

  adminListLogos: async (
    page: number = 1,
    pageSize: number = 20,
    search?: string
  ): Promise<AdminLogoListResponse> => {
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (search) params.search = search;
    const response = await api.get<AdminLogoListResponse>('/logos/admin/list', { params });
    return response.data;
  },

  adminGetLogo: async (logoId: string): Promise<LogoDetails> => {
    const response = await api.get<LogoDetails>(`/logos/admin/${logoId}`);
    return response.data;
  },

  adminUpdateLogo: async (logoId: string, data: AdminLogoUpdateRequest): Promise<AdminLogoResponse> => {
    const response = await api.put<AdminLogoResponse>(`/logos/admin/${logoId}`, data);
    return response.data;
  },

  adminDeleteLogo: async (logoId: string): Promise<AdminLogoResponse> => {
    const response = await api.delete<AdminLogoResponse>(`/logos/admin/${logoId}`);
    return response.data;
  },

  adminUploadLogoImage: async (logoId: string, imageFile: File): Promise<AdminLogoResponse> => {
    const formData = new FormData();
    formData.append('image_file', imageFile);
    const response = await api.post<AdminLogoResponse>(`/logos/admin/${logoId}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  adminApproveLogo: async (logoId: string): Promise<AdminLogoResponse> => {
    const response = await api.post<AdminLogoResponse>(`/logos/admin/${logoId}/approve`);
    return response.data;
  },

  adminRetireLogo: async (logoId: string): Promise<AdminLogoResponse> => {
    const response = await api.post<AdminLogoResponse>(`/logos/admin/${logoId}/retire`);
    return response.data;
  },

  adminRestoreLogo: async (logoId: string): Promise<AdminLogoResponse> => {
    const response = await api.post<AdminLogoResponse>(`/logos/admin/${logoId}/restore`);
    return response.data;
  },

  // Report a logo as incorrect/outdated
  reportLogo: async (logoId: string, reason?: string): Promise<{ status: string }> => {
    const response = await api.post<{ status: string }>(`/logos/${logoId}/report`, {
      reason: reason || undefined,
    });
    return response.data;
  },

  // Sync status
  getLogoSyncStatus: async (): Promise<{
    last_sync_at: string | null;
    status: string | null;
    records_updated: number;
    records_failed: number;
    next_scheduled: string;
    is_stale: boolean;
    message: string;
  }> => {
    const response = await api.get('/logos/admin/sync-status');
    return response.data;
  },

  triggerLogoSync: async (): Promise<AdminLogoResponse> => {
    const response = await api.post<AdminLogoResponse>('/logos/admin/sync/trigger');
    return response.data;
  },

  // Salesforce Account Lookup for logo submission
  salesforceLookup: async (accountId: string): Promise<SalesforceLookupResponse> => {
    const response = await api.get<SalesforceLookupResponse>(`/logos/salesforce-lookup/${accountId}`);
    return response.data;
  },
};

// Set access token for API calls
export const setApiAccessToken = (token: string | null) => {
  if (token) {
    localStorage.setItem('accessToken', token);
  } else {
    localStorage.removeItem('accessToken');
  }
};

export default api;
